import { TableExportPdfAdapter } from '../TableExportPdfAdapter';
import {
  ReportFilter,
  OpStructuresFilter,
  Translation,
  CollectionsEconomicAccountingFilter,
} from '../../types';
import { PdfReportType } from '../../state/exports/types';

type GetParametersTableArgs = {
  t: Translation;
  title: string;
  filters: ReportFilter;
  opsFilters: OpStructuresFilter;
  locale: Locale;
  parameters?: (keyof ReportFilter)[];
  opStructures?: (keyof OpStructuresFilter)[];
  pdfReportType?: PdfReportType;
  extraFilters?: CollectionsEconomicAccountingFilter;
};

const getSafeDate = (date?: { accept?: Date | null }) => date?.accept ?? '';

export const getParametersTable = ({
  t,
  title,
  filters,
  opsFilters,
  locale,
  parameters = [],
  opStructures = [],
  pdfReportType = undefined,
  extraFilters = undefined,
}: GetParametersTableArgs) => {
  let pdfGroupBy;
  if (pdfReportType === PdfReportType.economicAccounting && extraFilters?.groupBy) {
    pdfGroupBy = extraFilters.groupBy.map(i => t(`toolbar.groupBy.${i}`)).join(', ');
  } else {
    pdfGroupBy = filters.groupBy.map(i => t(`toolbar.groupBy.${i}`)).join(', ');
  }
  let pdfStartBusinessDate;
  let pdfEndBusinessDate;
  if (pdfReportType === PdfReportType.economicAccounting && extraFilters?.startBusinessDate) {
    pdfStartBusinessDate = getSafeDate(extraFilters.startBusinessDate);
    pdfEndBusinessDate = getSafeDate(extraFilters.endBusinessDate);
  } else {
    pdfStartBusinessDate = getSafeDate(filters.startBusinessDate);
    pdfEndBusinessDate = getSafeDate(filters.endBusinessDate);
  }

  const values = {
    header: t('dataGridFlexSales.exports.labels.parameterValue'),
    countryCode: t(`operativeStructure.countries.${filters.countryCode}`),
    stores: filters.stores.map(i => i.storeAcronym).join(', '),
    pos: filters.pos.join(', '),
    salesTypes: filters.salesTypes.map(i => i.saleTypeDescription).join(', '),
    segments: filters.segments.map(i => i.segmentDesc).join(', '),
    timeBand: filters.timeBand.join(', '),
    hourRange: filters.hourRange.join(', '),
    startAccountingDate: getSafeDate(filters.startAccountingDate),
    endAccountingDate: getSafeDate(filters.endAccountingDate),
    startOperationDate: getSafeDate(filters.startOperationDate),
    endOperationDate: getSafeDate(filters.endOperationDate),
    startBusinessDate: pdfStartBusinessDate,
    endBusinessDate: pdfEndBusinessDate,
    plu: filters.plu.join(', '),
    regionalMgmt: opsFilters.regionalMgmt.map(i => i.regionalMgmt).join(', '),
    region: opsFilters.region.map(i => i.region).join(', '),
    management: opsFilters.management.map(i => i.mgmt).join(', '),
    supervision: opsFilters.supervision.map(i => i.supervision).join(', '),
    agreement: opsFilters.agreement.map(i => i.agreementType).join(', '),
    company: opsFilters.company.map(i => i.companyName).join(', '),
    groupBy: pdfGroupBy,
    salesProductGroupBy: filters.salesProductGroupBy.map(i => t(`toolbar.groupBy.${i}`)).join(', '),
  };

  const adapter = new TableExportPdfAdapter<typeof values>([values], locale);

  adapter.addColumn({
    type: 'string',
    value: 'header',
    title: t('dataGridFlexSales.exports.labels.parameter'),
  });

  adapter.addColumn({
    type: 'string',
    value: 'countryCode',
    title: t('toolbar.labels.country'),
    available: () => parameters.includes('countryCode'),
  });

  adapter.addColumn({
    type: 'string',
    value: 'stores',
    title: t('toolbar.labels.store'),
    available: () => parameters.includes('stores'),
  });

  adapter.addColumn({
    type: 'string',
    value: 'pos',
    title: t('toolbar.labels.pos'),
    available: () => parameters.includes('pos'),
  });

  adapter.addColumn({
    type: 'string',
    value: 'salesTypes',
    title: t('toolbar.labels.sale-type'),
    available: () => parameters.includes('salesTypes'),
  });

  adapter.addColumn({
    type: 'string',
    value: 'segments',
    title: t('toolbar.labels.pos-type'),
    available: () => parameters.includes('segments'),
  });

  adapter.addColumn({
    type: 'string',
    value: 'timeBand',
    title: t('toolbar.labels.time-band'),
    available: () => parameters.includes('timeBand'),
  });

  adapter.addColumn({
    type: 'string',
    value: 'hourRange',
    title: t('toolbar.labels.hour-range'),
    available: () => parameters.includes('hourRange'),
  });

  adapter.addColumn({
    type: 'date',
    value: 'startAccountingDate',
    title: t('toolbar.labels.fromDateAccounting'),
    available: () => parameters.includes('startAccountingDate'),
  });

  adapter.addColumn({
    type: 'date',
    value: 'endAccountingDate',
    title: t('toolbar.labels.toDateAccounting'),
    available: () => parameters.includes('endAccountingDate'),
  });

  adapter.addColumn({
    type: 'date',
    value: 'startOperationDate',
    title: t('toolbar.labels.fromDateOperation'),
    available: () => parameters.includes('startOperationDate'),
  });

  adapter.addColumn({
    type: 'date',
    value: 'endOperationDate',
    title: t('toolbar.labels.toDateOperation'),
    available: () => parameters.includes('endOperationDate'),
  });

  adapter.addColumn({
    type: 'date',
    value: 'startBusinessDate',
    title: t('toolbar.labels.fromDateBusiness'),
    available: () => parameters.includes('startBusinessDate'),
  });

  adapter.addColumn({
    type: 'date',
    value: 'endBusinessDate',
    title: t('toolbar.labels.toDateBusiness'),
    available: () => parameters.includes('endBusinessDate'),
  });

  adapter.addColumn({
    type: 'string',
    value: 'plu',
    title: t('table.columns.plu'),
    available: () => parameters.includes('plu'),
  });

  adapter.addColumn({
    type: 'string',
    value: 'regionalMgmt',
    title: t('toolbar.labels.regionalMgmt'),
    available: () => opStructures.includes('regionalMgmt'),
  });

  adapter.addColumn({
    type: 'string',
    value: 'region',
    title: t('toolbar.labels.region'),
    available: () => opStructures.includes('region'),
  });

  adapter.addColumn({
    type: 'string',
    value: 'management',
    title: t('toolbar.labels.management'),
    available: () => opStructures.includes('management'),
  });

  adapter.addColumn({
    type: 'string',
    value: 'supervision',
    title: t('toolbar.labels.supervision'),
    available: () => opStructures.includes('supervision'),
  });

  adapter.addColumn({
    type: 'string',
    value: 'agreement',
    title: t('toolbar.labels.agreement'),
    available: () => opStructures.includes('agreement'),
  });

  adapter.addColumn({
    type: 'string',
    value: 'company',
    title: t('toolbar.labels.company'),
    available: () => opStructures.includes('company'),
  });

  adapter.addColumn({
    type: 'string',
    value: 'groupBy',
    title: t('toolbar.labels.groupBy'),
    available: () => parameters.includes('groupBy'),
  });

  adapter.addColumn({
    type: 'string',
    value: 'salesProductGroupBy',
    title: t('toolbar.labels.groupBy'),
    available: () => parameters.includes('salesProductGroupBy'),
  });

  return adapter.getTableTranspose({
    title,
    getColumnConfig: headers =>
      headers.map((_, i) => ({
        width: i ? 250 : 100,
        align: 'left',
      })),
  });
};
